import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  LayoutDashboard,
  CalendarCheck,
  Users,
  Shield,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Building2,
  User,
} from "lucide-react";
import { logout } from "../../../services/userService";
import { getUserProviderManagers } from "../../../services/staffingService";
import { useUser } from "../../../context/UserContext";

const NAV_ITEMS = [
  { path: "dashboard", label: "Overview",    icon: LayoutDashboard,   match: ["dashboard"] },
  { path: "dispatch",  label: "Dispatch",    icon: CalendarCheck,      match: ["dispatch"] },
  { path: "crm",       label: "Customers",   icon: Users,              match: ["crm"] },
  { path: "orm",       label: "Officers",    icon: Shield,             match: ["orm"] },
  { path: "team",      label: "Team",        icon: Users,              match: ["team"] },
  { path: "settings",  label: "Settings",    icon: SlidersHorizontal,  match: ["settings"] },
];

const AgencyDashboardSidebar = ({ isOpen, setIsOpen }) => {
  const location     = useLocation();
  const { providerCompanyId } = useParams();
  const { userInfo } = useUser();

  const [companyName,    setCompanyName]    = useState("Agency");
  const [companyInitial, setCompanyInitial] = useState("A");

  useEffect(() => {
    if (providerCompanyId) {
      getUserProviderManagers()
        .then((resp) => {
          const match = resp.data?.find(
            (m) => m.provider_company_id?._id === providerCompanyId
          );
          const name = match?.provider_company_id?.agency_name || "Agency";
          setCompanyName(name);
          setCompanyInitial(name.charAt(0).toUpperCase() || "A");
        })
        .catch(() => {});
    }
  }, [providerCompanyId]);

  // isOpen=true → collapsed (68px); isOpen=false → expanded (240px)
  const collapsed = isOpen;

  const isActive = (matchTokens) =>
    matchTokens.some((token) => location.pathname.includes(token));

  const initiateLogout = async () => {
    const resp = await logout();
    window.location.href = resp.data.logoutUrl;
  };

  const fullName =
    userInfo?.firstName
      ? `${userInfo.firstName} ${userInfo.lastName || ""}`.trim()
      : userInfo?.email || "Manager";

  return (
    <aside
      className={`flex h-screen flex-col border-r border-sidebar-border bg-sidebar transition-all duration-300 flex-shrink-0 font-inter ${
        collapsed ? "w-[68px]" : "w-[240px]"
      }`}
    >
      {/* Logo */}
      <div className="flex h-16 items-center border-b border-sidebar-border px-4">
        {collapsed ? (
          <div className="flex w-full justify-center">
            <img src={require('../../_asset/logo/icon.png')} alt="Officer" className="w-auto h-8 md:h-10 object-contain" />
          </div>
        ) : (
          <Link
            to={`/provider-company/${providerCompanyId}/dashboard`}
            className="flex items-center gap-2 no-underline"
          >
            <img src={require('../../_asset/logo/icon.png')} alt="Officer" className="w-auto h-8 md:h-10 object-contain" />
            <span className="text-lg font-bold text-[#404880]">
              Officer.app
            </span>
          </Link>
        )}
      </div>

      {/* Company pill */}
      <div className="border-b border-sidebar-border p-3">
        <Link
          to="/home"
          title="Switch agencies"
          className={`flex items-center gap-2 rounded-lg px-2 py-2.5 text-sm font-medium transition-colors no-underline cursor-pointer ${
            collapsed
              ? "justify-center"
              : "text-sidebar-foreground hover:bg-sidebar-accent"
          }`}
        >
          {collapsed ? (
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-brand-pink text-white font-semibold text-sm">
              {companyInitial}
            </div>
          ) : (
            <>
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-brand-pink/20 text-brand-pink">
                <Building2 size={16} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate text-xs font-semibold text-sidebar-foreground">
                  {companyName}
                </div>
                <div className="text-[10px] text-sidebar-foreground/60">
                  Agency portal
                </div>
              </div>
            </>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4">
        {NAV_ITEMS.map(({ path, label, icon: Icon, match }) => {
          const active = isActive(match);
          return (
            <Link
              key={path}
              to={`/provider-company/${providerCompanyId}/${path}`}
              title={collapsed ? label : ""}
              className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors no-underline ${
                collapsed ? "justify-center" : ""
              } ${
                active
                  ? "bg-brand-pink/10 text-brand-pink"
                  : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              }`}
            >
              <Icon size={20} strokeWidth={active ? 2.5 : 2} />
              {!collapsed && <span>{label}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User footer */}
      <div className="p-3">
        <div
          className={`rounded-lg py-2.5 ${
            collapsed
              ? "flex justify-center"
              : "grid grid-cols-[auto_1fr] grid-rows-2 gap-2 px-3"
          }`}
        >
          {userInfo?.profilePictureUrl ? (
            <img
              src={userInfo.profilePictureUrl}
              alt={fullName}
              className={`h-8 w-8 rounded-full object-cover flex-shrink-0 ${
                !collapsed ? "row-span-2" : ""
              }`}
            />
          ) : (
            <div
              className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-pink/20 text-brand-pink ${
                !collapsed ? "row-span-2" : ""
              }`}
            >
              <User size={16} />
            </div>
          )}

          {!collapsed && (
            <span className="truncate text-xs font-medium text-sidebar-foreground col-start-2">
              {fullName}
            </span>
          )}

          {!collapsed && (
            <button
              onClick={initiateLogout}
              className="flex items-center gap-1 text-left text-xs text-sidebar-foreground/60 transition-colors hover:text-sidebar-foreground col-start-2"
            >
              <LogOut size={12} />
              Log out
            </button>
          )}
        </div>
      </div>

      {/* Collapse toggle */}
      <div className="border-t border-sidebar-border p-3">
        <button
          onClick={() => setIsOpen && setIsOpen(!isOpen)}
          className="flex w-full items-center justify-center rounded-lg py-2 text-sidebar-foreground transition-colors hover:bg-sidebar-accent"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>
    </aside>
  );
};

export default AgencyDashboardSidebar;
